import type { AdminAiScanBusinessObjectProps } from "../../models/admin-ai-scans.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import {
  getAdminAiScanObjects,
  getAdminAiScanObject,
  getAdminAiScanReviewStatus,
  getAdminAiScanFindingSeverity,
  getAdminAiScanText,
} from "../../utils/admin-ai-scans.utils";
import AdminAiScanReviewTree from "./AdminAiScanReviewTree";
import AdminAiScanTreeSummary from "./AdminAiScanTreeSummary";
import { useState } from "react";

// Keep the inventory BO even when no scan exists, and retain every returned report for that BO.
const AdminAiScanBusinessObject = ({
  entry,
  featureReviewKey,
  findingFields,
  expansion,
  severity,
}: AdminAiScanBusinessObjectProps) => {
  const [open, setOpen] = useState(expansion !== "closed");
  const findingsCount = entry.reports.reduce(
    (count, file) =>
      count +
      getAdminAiScanObjects(file.report.findings).filter(
        (finding) => !severity || getAdminAiScanFindingSeverity(finding) === severity,
      ).length,
    0,
  );
  const status = entry.reports.length
    ? entry.reports.map((file) => getAdminAiScanReviewStatus(file.report)).join(" / ")
    : "NOT_SCANNED";

  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className={adminAiScansStyles.treeBranch}
      data-test="admin-ai-scan-business-object"
    >
      <AdminAiScanTreeSummary
        label={entry.label}
        id={entry.businessObjectId}
        meta={status.replace(/_/g, " ")}
        counts={[{ label: "Findings", value: findingsCount }]}
      />
      <div className={adminAiScansStyles.treeChildren}>
        {open && entry.reports.length === 0 && (
          <AdminAiScanReviewTree
            inventory={entry.inventory}
            report={null}
            featureReviewKey={featureReviewKey}
            findingFields={findingFields}
            expansion={expansion}
            severity={severity}
          />
        )}
        {open &&
          entry.reports.map((file) => {
            const meta = getAdminAiScanObject(file.report.report);

            return (
              <section key={file.path} className="min-w-0">
                <AdminAiScanReviewTree
                  inventory={entry.inventory}
                  report={file.report}
                  featureReviewKey={featureReviewKey}
                  findingFields={findingFields}
                  expansion={expansion}
                  severity={severity}
                />
                <details
                  className="border-t border-slate-200 p-3 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-300"
                  data-test="admin-ai-scan-report-source"
                >
                  <summary className="cursor-pointer py-2 focus-visible:outline-green">
                    Report source and execution
                  </summary>
                  <div className="space-y-2 break-words leading-6">
                    <p>Business object: {entry.businessObjectId}</p>
                    <p>Report: {file.path}</p>
                    <p>
                      Generated: {getAdminAiScanText(meta.generatedAt, "Unspecified")} · Confidence:{" "}
                      {getAdminAiScanText(meta.scannerConfidence, "Unspecified")} · Schema:{" "}
                      {getAdminAiScanText(file.report.schemaVersion, "Unspecified")}
                    </p>
                    <p>
                      Execution: {getAdminAiScanText(getAdminAiScanObject(file.report.execution).mode, "Unspecified")} ·
                      Shell completed: {getAdminAiScanText(file.report.shellCompleted, "Unspecified")}
                    </p>
                    <p>Review status and coverage are reported separately.</p>
                  </div>
                </details>
              </section>
            );
          })}
      </div>
    </details>
  );
};

export default AdminAiScanBusinessObject;
